import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LegalLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [location.pathname]);

    const tabs = [
        { name: 'Privacy Policy', path: '/privacy' },
        { name: 'Terms of Service', path: '/terms' },
    ];

    return (
        <div className="min-h-screen bg-[#0F172A] text-[#CBD5E1] font-sans selection:bg-primary-500/30 overflow-x-hidden">
            {/* Background Decorative Blobs */}
            <div className="fixed top-0 left-1/4 w-[500px] h-[500px] bg-primary-500/5 blur-[120px] rounded-full pointer-events-none" />
            <div className="fixed bottom-0 right-1/4 w-[500px] h-[500px] bg-secondary-500/5 blur-[120px] rounded-full pointer-events-none" />

            {/* Tabs Navigation */}
            <div className="pt-40 pb-12 flex justify-center gap-3 px-4 relative z-10 animate-fade-in">
                <div className="bg-dark-900/50 backdrop-blur-xl border border-white/5 p-1.5 rounded-full flex gap-1 shadow-2xl">
                    {tabs.map((tab) => {
                        const isActive = location.pathname === tab.path;
                        return (
                            <Link
                                key={tab.path}
                                to={tab.path}
                                className={`px-6 md:px-8 py-2.5 rounded-full text-xs md:text-sm font-bold transition-all duration-500 ${isActive
                                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg shadow-primary-500/25'
                                        : 'text-dark-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                {tab.name}
                            </Link>
                        );
                    })}
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 pb-24 relative z-10">
                {children}

                {/* Shared Contact Footer */}
                <div className="mt-24 p-8 md:p-16 rounded-[40px] bg-gradient-to-br from-[#1E1B4B]/80 to-[#0F172A]/80 backdrop-blur-sm border border-white/10 text-center animate-slide-up relative overflow-hidden group">
                    <div className="absolute inset-0 bg-primary-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-[80px]" />

                    <div className="relative z-10">
                        <h3 className="text-2xl md:text-3xl font-black text-white mb-3">Questions about our legal terms?</h3>
                        <p className="text-dark-400 mb-10 max-w-md mx-auto font-medium">We believe in plain English over legal jargon. Reach out anytime — we usually respond within 24 hours.</p>
                        <a
                            href="mailto:hello@sharevisuals.com"
                            className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-black hover:scale-105 transition-all duration-300 shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_60px_rgba(124,58,237,0.5)] active:scale-95"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            <span>hello@sharevisuals.com</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Simple Footer */}
            <div className="border-t border-white/5 py-12 text-center">
                <p className="text-dark-500 text-sm font-medium">
                    © 2026 <span className="text-white font-bold">Share Visuals</span>. All rights reserved.
                </p>
            </div>
        </div>
    );
};

export default LegalLayout;
